import { useEffect, useState } from "react";
import { Button, Dropdown, Spinner } from "react-bootstrap";
import { listQuoteTemplates, applyQuoteTemplate } from "../../services/apiClient.js";

// The Quote tab's "Create ... From Template" control — 1.0's links, now driven by
// System Data → Quote Templates.
//
// Two behaviours, because the templates resolve two different ways:
//   scope / notes → ONE template, chosen from the job's type. A plain button.
//   estimates     → several named templates (Overheads, VC Callout) that all
//                   apply to any job, so the user picks. A dropdown.
// Inactive templates never appear; the server refuses them too, so a stale page
// cannot slip one onto a quote.

function ApplyTemplateButton({ claimId, target, label = "Create From Template", onDone, onError }) {
  const [templates, setTemplates] = useState(null); // null = still loading
  const [busy, setBusy] = useState(false);
  const picks = target === "estimates";

  useEffect(() => {
    listQuoteTemplates()
      .then((rows) => setTemplates(rows.filter((t) => t.is_active && t.target === target)))
      .catch(() => setTemplates([]));
  }, [target]);

  async function apply(template) {
    // Applying writes real lines — and for a priced template, real money. Worth
    // one confirm, especially as it is only ever offered on an empty list.
    const what = template ? `"${template.name}"` : "the template for this job type";
    if (!window.confirm(`Fill this list from ${what}?`)) return;
    setBusy(true);
    onError?.("");
    try {
      const result = await applyQuoteTemplate({
        claim_id: claimId,
        target,
        template_id: template?.id,
      });
      await onDone?.(result);
    } catch (err) {
      // The server's messages are already written for a user ("This job already
      // has Scope of Works — a template only fills an empty list."), so pass
      // them straight through rather than inventing a worse one.
      onError?.(err.response?.data?.error || "Unable to apply the template.");
    } finally {
      setBusy(false);
    }
  }

  // Nothing set up for this target yet — say nothing rather than offer a button
  // that can only fail.
  if (templates === null || templates.length === 0) return null;

  if (busy) {
    return (
      <Button size="sm" variant="outline-primary" disabled>
        <Spinner size="sm" animation="border" className="me-1" /> Applying…
      </Button>
    );
  }

  if (!picks) {
    return (
      <Button size="sm" variant="outline-primary" onClick={() => apply(null)}>
        {label}
      </Button>
    );
  }

  return (
    <Dropdown>
      <Dropdown.Toggle size="sm" variant="outline-primary" id={`apply-template-${target}`}>
        {label}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {templates.map((t) => (
          <Dropdown.Item key={t.id} onClick={() => apply(t)}>
            {t.name}
            <span className="text-muted ms-2 small">{t.line_count} lines</span>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default ApplyTemplateButton;
